import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import type { PackageItem } from "@/types/package";
import toast from "react-hot-toast";
import { useUserPackageRegistration } from "@/hooks/useUserPackageRegistration";
import { CheckCircle2, MousePointerClick, Info, Sparkles, Ticket, XCircle } from "lucide-react"; // ✅ Tambah XCircle
import InfoKonvensiFeesModal from "./paket/InfoKonvensiFessModal";

type Props = {
    selected: number | null;
    onChange: (id: number) => void;
    data: PackageItem[];
    periodId: number;
    participantId: number;
    onSavingChange?: (val: boolean) => void;
    parentName?: string;
};

export default function CityTourSelector({
    selected,
    onChange,
    data,
    participantId,
    periodId,
    parentName,
}: Props) {
    const [detailPkg, setDetailPkg] = useState<PackageItem | null>(null);
    const { saveOrUpdateRegistration } = useUserPackageRegistration(participantId, periodId);

    const handleSelect = async (pkg: PackageItem) => {
        // ✅ PROTEKSI: Cegah klik jika kuota habis
        if (pkg.is_quota === 1 && pkg.available_quota <= 0) {
            toast.error(`Maaf, kuota untuk City Tour ${pkg.name} sudah habis.`);
            return;
        }

        onChange(pkg.id);
        toast.promise(
            saveOrUpdateRegistration.mutateAsync({
                period_id: periodId,
                package_id: pkg.id,
                note: `Peserta memilih paket ${pkg.name}`,
            }),
            {
                loading: "Menyimpan pilihan City Tour...",
                success: "City Tour berhasil dipilih!",
                error: "Gagal menyimpan pilihan.",
            }
        );
    };

    return (
        <section className="mx-auto max-w-7xl antialiased relative mt-10">

            {/* --- Info Section Header --- */}
            <div className="flex items-center justify-between px-2 mb-6">
                <div className="flex items-center gap-3">
                    <div className="h-6 w-1 bg-emerald-500 rounded-full shadow-sm shadow-emerald-500/30" />
                    <h2 className="text-xs font-black uppercase tracking-[0.3em] text-slate-500 dark:text-slate-400">
                        Pilihan Sub-Paket
                    </h2>
                </div>
                <div className="hidden md:flex items-center gap-2 text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase italic">
                    <Sparkles className="w-3 h-3 text-emerald-500" /> Silakan pilih salah satu opsi di bawah ini
                </div>
            </div>
            {/* --------------------------- */}

            <div className="grid gap-6 items-stretch [grid-template-columns:repeat(auto-fit,minmax(280px,1fr))]">
                {data.map((pkg) => {
                    const isActive = selected === pkg.id;
                    // const price = Number(pkg.price);
                    // const isFree = price === 0;

                    // ✅ LOGIKA SOLD OUT
                    const isSoldOut = pkg.is_quota === 1 && pkg.available_quota <= 0;

                    return (
                        <motion.div
                            key={pkg.id}
                            whileHover={isSoldOut ? {} : { y: -8 }} // Matikan efek hover naik jika habis
                            className={`group relative flex flex-col h-full overflow-hidden rounded-[1.5rem] border transition-all duration-300
                                ${isSoldOut
                                    ? "border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-900/50 cursor-not-allowed opacity-90" // Style jika Habis
                                    : isActive
                                        ? "border-emerald-500 bg-emerald-50/30 dark:bg-emerald-900/10 ring-2 ring-emerald-500/20 shadow-lg shadow-emerald-500/10 cursor-pointer" // Style jika Dipilih
                                        : "border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 hover:shadow-xl hover:border-blue-200 dark:hover:border-blue-900 cursor-pointer" // Style Normal
                                }`}
                        >
                            {/* Selected Badge */}
                            {isActive && !isSoldOut && (
                                <motion.div
                                    initial={{ opacity: 0, x: 10 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    className="absolute top-4 right-4 z-20 flex items-center gap-1.5 rounded-full bg-emerald-500 px-3 py-1 text-[10px] font-bold text-white shadow-lg shadow-emerald-500/40 uppercase tracking-widest"
                                >
                                    <CheckCircle2 className="h-3 w-3" />
                                    <span>Dipilih</span>
                                </motion.div>
                            )}

                            {/* ✅ Sold Out Badge */}
                            {isSoldOut && (
                                <div className="absolute top-4 right-4 z-20 flex items-center gap-1.5 rounded-full bg-rose-500 px-3 py-1 text-[10px] font-bold text-white shadow-lg shadow-rose-500/40 uppercase tracking-widest">
                                    <XCircle className="h-3 w-3" />
                                    <span>Habis</span>
                                </div>
                            )}

                            {/* Image Section */}
                            <div className="relative h-48 w-full shrink-0 overflow-hidden" onClick={() => handleSelect(pkg)}>
                                <img
                                    src={pkg.photo_url}
                                    alt={pkg.name}
                                    className={`h-full w-full object-cover transition-transform duration-700 
                                        ${isSoldOut ? 'grayscale contrast-75 opacity-80' : 'group-hover:scale-110'} 
                                        ${isActive ? 'scale-105' : ''}`}
                                />
                                <div className={`absolute inset-0 transition-opacity duration-300 
                                    ${isSoldOut ? 'bg-slate-900/20' : isActive ? '' : 'bg-emerald-500/10 group-hover:bg-transparent'}`}
                                />
                            </div>

                            {/* Content Section */}
                            <div className="p-6 space-y-4 flex flex-col flex-1">

                                {/* Title, Description & QUOTA */}
                                <div className="space-y-3 flex-1" onClick={() => handleSelect(pkg)}>
                                    <div className="space-y-1">
                                        <h4 className={`text-md font-semibold transition-colors duration-300 
                                            ${isSoldOut ? "text-slate-500 dark:text-slate-400"
                                                : isActive ? "text-emerald-700 dark:text-emerald-400"
                                                    : "text-slate-900 dark:text-white"}`}>
                                            {pkg.name}
                                        </h4>
                                        <p className="text-xs font-medium text-slate-500 dark:text-slate-400 line-clamp-2">
                                            {pkg.description}
                                        </p>
                                    </div>

                                    {/* Info Kuota dengan desain pill/badge */}
                                    {pkg.is_quota === 1 && (
                                        <div className={`inline-flex flex-wrap items-center gap-1.5 rounded-md px-2.5 py-1.5 border 
                                            ${isSoldOut
                                                ? "bg-rose-50 border-rose-200 dark:bg-rose-500/10 dark:border-rose-500/20"
                                                : "bg-orange-50 border-orange-200 dark:bg-orange-500/10 dark:border-orange-500/20"}`}>
                                            <Ticket className={`h-3.5 w-3.5 ${isSoldOut ? 'text-rose-500' : 'text-orange-500'}`} />
                                            <span className={`text-[11px] font-bold uppercase tracking-wide ${isSoldOut ? 'text-rose-600 dark:text-rose-400' : 'text-orange-600 dark:text-orange-400'}`}>
                                                Total Kuota: {pkg.quota}
                                            </span>
                                            <span className={`text-[11px] font-bold uppercase tracking-wide ${isSoldOut ? 'text-rose-600 dark:text-rose-400' : 'text-orange-600 dark:text-orange-400'}`}>
                                                Sisa Kuota: {pkg.available_quota}
                                            </span>
                                        </div>
                                    )}
                                </div>

                                {/* Info Harga & Icon Seleksi */}
                                <div className="flex items-center justify-between pt-2" onClick={() => handleSelect(pkg)}>
                                    <div className="flex flex-col">
                                        {/* <span className={`text-[10px] font-bold uppercase tracking-widest ${isActive && !isSoldOut ? 'text-emerald-600/60 dark:text-emerald-400/50' : 'text-slate-400'}`}>
                                            Harga
                                        </span> */}
                                        {/* <span className={`text-lg font-black transition-colors 
                                            ${isSoldOut ? 'text-slate-400 dark:text-slate-500 line-through'
                                                : isActive ? 'text-emerald-700 dark:text-emerald-400'
                                                    : 'text-slate-900 dark:text-white'}`}>
                                            {isFree ? (
                                                "GRATIS"
                                            ) : (
                                                `Rp ${price.toLocaleString("id-ID")}`
                                            )}
                                        </span> */}
                                        <span className={`text-[10px] font-bold uppercase tracking-widest ${isActive && !isSoldOut ? 'text-emerald-600/60 dark:text-emerald-400/50' : 'text-slate-400'}`}>
                                            Kategori Paket
                                        </span>
                                        <span className={`text-lg font-black transition-colors 
                                            ${isSoldOut ? 'text-slate-400 dark:text-slate-500 line-through'
                                                : isActive ? 'text-emerald-700 dark:text-emerald-400'
                                                    : 'text-slate-900 dark:text-white'}`}>
                                            <span className={`text-sm font-bold flex items-center gap-1 ${isSoldOut ? 'text-slate-400' : 'text-blue-600 dark:text-blue-400'}`}>
                                                {parentName && (
                                                    <>
                                                        {parentName}
                                                    </>
                                                )}
                                            </span>
                                        </span>
                                    </div>

                                    {/* Icon Klik */}
                                    <div
                                        className={`flex shrink-0 items-center justify-center w-11 h-11 rounded-xl transition-all duration-300 
                                        ${isSoldOut
                                                ? "bg-slate-200 dark:bg-slate-800 text-slate-400"
                                                : isActive
                                                    ? "bg-emerald-500 text-white shadow-lg"
                                                    : "bg-slate-50 dark:bg-slate-700/50 text-slate-400 group-hover:bg-blue-600 group-hover:text-white"}`}>
                                        {isSoldOut ? <XCircle className="w-5 h-5" /> : isActive ? <CheckCircle2 className="w-5 h-5" /> : <MousePointerClick className="w-5 h-5" />}
                                    </div>
                                </div>

                                {/* Divider Lembut */}
                                <div className={`h-px w-full ${isActive && !isSoldOut ? 'bg-emerald-100 dark:bg-emerald-800/40' : 'bg-slate-100 dark:bg-slate-700/50'}`} />

                                {/* Tombol Detail Paket */}
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setDetailPkg(pkg);
                                    }}
                                    className={`group/btn mt-auto flex w-full items-center justify-center gap-2 rounded-xl py-2.5 text-[11px] font-bold transition-all duration-300 border 
                                        ${isSoldOut
                                            ? "bg-slate-100 dark:bg-slate-800/50 text-slate-500 border-slate-200 dark:border-slate-700 hover:bg-slate-200 dark:hover:bg-slate-800"
                                            : "bg-blue-50 dark:bg-slate-900/40 text-blue-500 dark:text-slate-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:text-blue-600 dark:hover:text-blue-400 border-slate-100 dark:border-slate-800"}`}
                                >
                                    <Info className="h-4 w-4 transition-transform group-hover/btn:scale-110" />
                                    <span className="tracking-widest uppercase">Lihat Detail Paket</span>
                                </button>
                            </div>
                        </motion.div>
                    );
                })}
            </div>

            {/* Render Modal */}
            <AnimatePresence>
                {detailPkg && (
                    <InfoKonvensiFeesModal
                        pkg={detailPkg}
                        onClose={() => setDetailPkg(null)}
                    />
                )}
            </AnimatePresence>
        </section>
    );
}